import { TFunction } from 'i18next';
import {
  Documentation,
  DocumentationValidationStatusEnum,
} from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import UserPreviewCell from '@lib/components/Table/commonComponents/UserPreviewCell/UserPreviewCell';
import DocumentationValidationStatusChip from '@lib/components/DocumentationValidationStatusChip/DocumentationValidationStatusChip';
import { CellProps, Column } from '@lib/components/Table/types';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateFormat } from '@lib/enums/dateTime';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import DocumentFavoriteCell from './DocumentFavoriteCell';
import RenewalDeadlineCell from './RenewalDeadlineCell';
import DocumentCodeCell from './DocumentCodeCell';
import DocumentNameCell from './DocumentNameCell';
import DocumentCategoryCell from './DocumentCategoryCell';
import DocumentTagsCell from './DocumentTagsCell';

type ColumnsArgs = {
  isApprovals?: boolean;
  isViewLocked?: boolean;
  isAiDetectionEnabled?: boolean;
  credits?: AiCredits | null;
  onFavoriteCompleted: (wasFavorited: boolean) => void;
  onOpen: (item: Documentation) => void;
  onValidationStatusChange?: (
    id: string,
    newStatus: DocumentationValidationStatusEnum,
  ) => void;
  t: TFunction<'translation', undefined>;
};

const PREMIUM_COLUMN_IDS = new Set([
  'favorited',
  'categoryName',
  'renewalDeadlineDate',
  'validationStatus',
]);

export default function getDocumentsTableColumns({
  onFavoriteCompleted,
  onOpen,
  onValidationStatusChange,
  isApprovals,
  isViewLocked,
  isAiDetectionEnabled,
  credits,
  t,
}: ColumnsArgs) {
  const result: Column<Documentation>[] = [
    {
      id: 'favorited',
      Header: '',
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentFavoriteCell item={item} onCompleted={onFavoriteCompleted} />
      ),
      width: 56,
      minWidth: 56,
      maxWidth: 56,
    },
    {
      id: 'name',
      Header: t('name'),
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentNameCell
          item={item}
          onOpen={onOpen}
          isAiDetectionEnabled={isAiDetectionEnabled}
          credits={credits}
        />
      ),
      minWidth: 250,
      maxWidth: 250,
      width: 250,
      sortable: true,
    },
    {
      id: 'code',
      Header: t('code'),
      accessor: 'code',
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentCodeCell
          item={item}
          isAiDetectionEnabled={isAiDetectionEnabled}
          credits={credits}
        />
      ),
      width: 120,
      minWidth: 120,
    },
    {
      id: 'categoryName',
      Header: t('category'),
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentCategoryCell
          item={item}
          isAiDetectionEnabled={isAiDetectionEnabled}
          credits={credits}
        />
      ),
      minWidth: 120,
      maxWidth: 250,
      sortable: true,
    },
    {
      id: 'updatedAt',
      Header: t('last-updated'),
      accessorFn: ({ item }: CellProps<Documentation>) =>
        item.updatedAt ? getFormattedDate(item.updatedAt, dateFormat) : '-',
      Cell: (props: CellProps<Documentation>) => <TextCell {...props} />,
      width: 150,
      minWidth: 120,
      sortable: true,
    },
    {
      id: 'renewalDeadlineDate',
      Header: t('planned-update'),
      accessorFn: ({ item }: CellProps<Documentation>) =>
        item.renewalDeadlineDate
          ? getFormattedDate(item.renewalDeadlineDate, dateFormat)
          : '-',
      Cell: (props: CellProps<Documentation>) => (
        <RenewalDeadlineCell {...props} />
      ),
      width: 150,
      minWidth: 120,
      sortable: true,
    },
    {
      id: 'validationStatus',
      Header: t('validation'),
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentationValidationStatusChip
          documentation={item}
          onValidationStatusChangeHandler={
            item.canUpdateValidationStatus?.value
              ? onValidationStatusChange
              : undefined
          }
        />
      ),
      width: 170,
      minWidth: 120,
      sortable: true,
    },
    {
      id: 'tags',
      Header: t('tags'),
      Cell: ({ item }: CellProps<Documentation>) => (
        <DocumentTagsCell
          item={item}
          isAiDetectionEnabled={isAiDetectionEnabled}
          credits={credits}
        />
      ),
      maxWidth: 350,
      minWidth: 250,
      width: 350,
    },
    {
      id: 'createdByFullName',
      Header: t('uploaded-by'),
      Cell: (props: CellProps<Documentation>) => {
        const { item } = props;
        const { createdBy } = item;
        if (!createdBy) return <TextCell {...props} value="-" />;
        return (
          <UserPreviewCell
            avatar={
              createdBy.avatarAttached?.urls?.small ||
              createdBy.avatarAttached?.urls?.original
            }
            name={`${createdBy.firstName} ${createdBy.lastName}`}
            email={createdBy.email}
          />
        );
      },
      minWidth: 308,
      width: 308,
      sortable: true,
    },
  ];
  const filtered = result.filter(({ id }) => {
    if (isViewLocked && typeof id === 'string' && PREMIUM_COLUMN_IDS.has(id))
      return false;
    return true;
  });
  if (isApprovals) {
    return filtered.filter(({ id }) => id !== 'status');
  }
  return filtered;
}
